---
title: "Connect Gmail"
description: "Connecting Gmail lets Chosen send candidate email from your real address, track replies, and run the scheduling assistant. How to connect, and what it can see."
category: "getting-started"
keywords:
  - "connect Gmail ATS"
  - "Gmail integration recruiting"
  - "send recruiting email"
  - "recruiting inbox sync"
schema: "HowTo"
nav:
  section: "getting-started"
  order: 20
  parent: "/getting-started"
---

Connecting Gmail lets Chosen send and receive email as *you*, from your real address. Candidates reply to a person, not a `noreply@`. This page is how to connect it and what Chosen can see once you do.

## What it unlocks

Three features only work with Gmail connected:

- **Outreach** — sourcing emails go out from your inbox, so replies thread normally and land where you'd expect.
- **Reply tracking** — when a candidate replies, Chosen links the message to their record instead of losing it in your inbox.
- **The scheduling assistant** — it reads scheduling replies ("can we do Tuesday instead?") and drafts responses. No inbox access, no assistant.

## How to connect

<Steps>
  <li>Go to **Settings → Integrations**.</li>
  <li>Click **Connect** next to Gmail.</li>
  <li>Sign in with Google and grant access. Connect the address you actually recruit from.</li>
</Steps>

Working in the recruiter portal instead? The same card lives at **Settings → Email** there. It's one connection either way — your Gmail follows you, not the workspace.

## It's per-person, not per-company

Each recruiter connects their own Gmail. There's no single org-wide inbox — your outreach sends from your address, your teammate's from theirs. That's deliberate: candidates should hear from a person they can build a relationship with.

By default, the candidate emails Chosen syncs are **shared with your team**, so when a candidate gets passed between recruiters the history goes along. A message that came through a teammate's mailbox says **via** their name on the thread — it never reads as if it came from you. If you'd rather keep your threads to yourself, turn off **Share emails with other org members** on the integration; then only you see the emails synced through your Gmail.

<Callout type="warning" title="Outreach and scheduling stop if Gmail disconnects">
Google access tokens expire, and revoking access from your Google account disconnects Chosen too. If outreach stops sending or the scheduling assistant goes quiet, check **Settings → Integrations** first — a greyed-out Gmail is the usual cause. Reconnecting takes ten seconds.
</Callout>

## What Chosen reads

Chosen reads scheduling-shaped messages and replies to threads it started — not your weekend plans. If you'd rather keep the scope narrower, you can revoke the broader permission from your Google account at any time; the trade-off is that the scheduling assistant gets less useful.

## Related

You'll connect [Google Calendar](/getting-started/integrations/google-calendar) next — together they're what the [scheduling](/guides/scheduling) features run on.
